import { DEFAULT_SEED, DEFAULT_SEEDS, generatePalette, type ColorScheme } from "@/lib/color-scheme";
import { DEFAULT_FONT_SCHEME } from "@/lib/fonts";
import type { ConfigDoc, FontScheme, SaveTemplateReq, Template } from "@/lib/types";

/** Sentinel a site stores once its colors or fonts were hand-edited. */
export const CUSTOM_TEMPLATE_ID = "custom";

/** Route to the create-template screen. */
export const TEMPLATE_NEW_PATH = "/templates/new";

/** Builds the edit route for a template id. */
export function buildTemplateEditPath(id: string): string {
  return `/templates/${encodeURIComponent(id)}/edit`;
}

/** The editable working copy of a template used by the editor screens. */
export interface TemplateDraft {
  name: string;
  colorScheme: ColorScheme;
  fontScheme: FontScheme;
}

/** A blank draft seeded with the default palette + font pairing. */
export function createEmptyTemplateDraft(): TemplateDraft {
  return {
    name: "",
    colorScheme: {
      seeds: DEFAULT_SEEDS,
      palette: generatePalette(DEFAULT_SEEDS),
    },
    fontScheme: DEFAULT_FONT_SCHEME,
  };
}

/** Converts a stored template into an editable draft. */
export function templateToDraft(template: Template): TemplateDraft {
  const seed = template.palette.primary ?? DEFAULT_SEED;
  return {
    name: template.name,
    colorScheme: { seeds: { seed }, palette: template.palette },
    fontScheme: template.fontScheme,
  };
}

/** Builds a save request from a draft (only the palette is persisted). */
export function draftToSaveTemplateReq(draft: TemplateDraft, id?: string): SaveTemplateReq {
  return {
    id,
    name: draft.name.trim(),
    palette: draft.colorScheme.palette,
    fontScheme: draft.fontScheme,
  };
}

/**
 * A site's live look, straight from its config — colorScheme/fontScheme
 * are already resolved values (copied in verbatim when a template or AI
 * look is applied), never a template reference to re-look-up. Falls back
 * to the uploader's built-in defaults for a brand-new site with no look
 * applied yet (mirrors createEmptyTemplateDraft above).
 */
export function resolveCurrentLook(config: ConfigDoc | null | undefined): {
  palette: Record<string, string>;
  fontScheme: FontScheme;
} {
  return {
    palette: config?.colorScheme?.palette ?? generatePalette(DEFAULT_SEEDS),
    fontScheme: config?.fontScheme ?? DEFAULT_FONT_SCHEME,
  };
}
